import {
  AllFlowsDefinition,
  FlowAction,
  FlowDefinition,
  FunnelState,
  FormAnswer,
  MoveFlowAction,
  PaymentDetailsData,
} from 'scenes/common/types';
import { MotorFormValues } from 'scenes/motor/hooks/useActions/types';
import { MotorApiResponses } from 'scenes/motor/actions/types';

import { Driver, MotorDriverSubSection } from './addDriver/types';
import { CoverOptionsData, MotorCoverSubSection } from './coverOptions/types';

import { MotorQuoteData, MotorQuoteSubSection } from './quoteDetails/types';
import {
  MotorPolicySubSection,
  PolicyDetailsData,
} from './policyDetails/types';
import { PolicyHolder } from './addPolicyHolder/types';
import { ReviewDetailsData } from './reviewDetails/types';

export enum MotorFlowType {
  QuoteDetails = 'quoteDetails',
  AddDriver = 'addDriver',
  CoverOptions = 'coverOptions',
  PolicyDetails = 'policyDetails',
  AddPolicyHolder = 'addPolicyHolder',
  PaymentDetails = 'paymentDetails',
  ReviewDetails = 'reviewDetails',
  ReviewDriver = 'reviewDriver',
}

export enum MotorGroupStepType {
  CarDetails = 'Car Details',
  DriverDetails = 'Driver Details',
  CarUsage = 'Car Usage',
  OtherDetails = 'Other Details',
  Quote = 'Quote',
  Customise = 'Customise',
  PolicyDetails = 'Policy Details',
  Payment = 'Payment',
}

export enum MotorStepType {
  Name = 'name',
  GenderAndDateOfBirth = 'gender-and-date-of-birth',
  OvernightParking = 'overnight-parking',
  RegoSearch = 'rego-search',
  VehicleDetails = 'vehicle-details',
  YearsDriving = 'years-driving',
  ClaimsMade = 'claims-made',
  PayableClaims = 'payable-claims',
  RecentInsurer = 'recent-insurer',
  UsedForBusiness = 'used-for-business',
  BusinessType = 'business-type',
  Finance = 'finance',
  VehicleFinancier = 'vehicle-financier',
  AddDriver = 'add-driver',
  PolicyStartDate = 'policy-start-date',
  VehicleManualSelect = 'vehicle-manual-select',
  FlybuysNumber = 'flybuys-number',
  PromoCode = 'promo-code',
  QuotePrice = 'quote-price',
  Excess = 'excess',
  MarketAgreedValue = 'market-agreed-value',
  OptionalExtras = 'optional-extras',
  PersonalOrBusinessPolicy = 'personal-business-policy',
  BusinessNameAndAbn = 'business-name-and-abn',
  BusinessRegisteredForGst = 'business-registered-for-gst',
  PersonalPolicyContact = 'personal-policy-contact',
  AddPolicyHolder = 'add-policy-holder',
  BusinessContact = 'business-contact',
  ContactPhoneNumber = 'contact-phone-number',
  ContactEmailAddress = 'contact-email-address',
  PostalAddress = 'postal-address',
  DifferentPostalAddress = 'different-postal-address',
  DifferentPostalAddressManual = 'different-postal-address-manual',
  LockedBagAddress = 'locked-bag-address',
  CoverForDriversUnder25 = 'cover-for-drivers-under-25',
  PreExistingDamage = 'pre-existing-damage',
  Modifications = 'modifications',
  ModificationSelection = 'modification-selection',
  SpecialFeatures = 'special-features',
  SpecialDetails = 'special-details',
  InsuredSincePossessed = 'insured-since-possessed',
  MissingRegoStep = 'missing-rego-step',
  TermsAndConditions = 'terms-and-conditions',
  Summary = 'summary',
  PaymentFrequency = 'payment-frequency',
  PaymentDate = 'payment-date',
  PaymentMethod = 'payment-method',
  DirectDebitPayment = 'direct-debit-payment',
  CreditCardPayment = 'credit-card-payment',
  PaymentConfirmation = 'payment-confirmation',
  ReviewPolicyStartDate = 'review-policy-start-date',
  ReviewDriverDetails = 'review-driver-details',
  addDriverName = 'add-driver-name',
}

export enum MotorMoveFlows {
  RemoveDriver = 'removeDriver',
  EditDriver = 'editDriver',
  EditReviewDriver = 'editReviewDriver',
  EditQuoteDetails = 'editQuoteDetails',
  EditPolicyDetails = 'editPolicyDetails',
  EditCoverDetails = 'editCoverDetails',
}

export type MotorFunnelState = FunnelState<MotorFlowType, MotorStepType> & {
  [MotorFlowType.QuoteDetails]: MotorQuoteData;
  [MotorFlowType.AddDriver]: Driver & { index: number };
  [MotorFlowType.CoverOptions]: CoverOptionsData;
  [MotorFlowType.PolicyDetails]: PolicyDetailsData & {
    policyHolders: PolicyHolder[];
  };
  [MotorFlowType.AddPolicyHolder]: PolicyHolder & { index: number };
  [MotorFlowType.PaymentDetails]: PaymentDetailsData;
  [MotorFlowType.ReviewDetails]: ReviewDetailsData;
  [MotorFlowType.ReviewDriver]: Driver & { index: number };
  apiResponses: MotorApiResponses;
};

export type MotorFlowDefinition = FlowDefinition<
  MotorFormValues,
  MotorFlowType,
  MotorStepType,
  MotorFunnelState
>;
export type MotorAllFlowDefinition = AllFlowsDefinition<
  MotorFormValues,
  MotorFlowType,
  MotorStepType,
  MotorFunnelState
>;
export type MotorFlowAction = FlowAction<MotorFormValues, MotorStepType>;
export type MotorFormAnswer = FormAnswer<MotorFormValues, MotorStepType> & {
  sliceToUpdate?: MotorFlowType;
};
export type MotorMoveFlowAction = MoveFlowAction<
  MotorFormValues,
  MotorMoveFlows,
  MotorStepType
> & { sliceToUpdate?: MotorFlowType };

export type MotorSubSection =
  | MotorQuoteSubSection
  | MotorDriverSubSection
  | MotorPolicySubSection
  | MotorCoverSubSection;

export enum MotorSection {
  CarDetails = 'Car details',
  CoverDetails = 'Cover details',
  MainDriver = 'Main driver',
  PolicyDetails = 'Policy details',
  AdditionalDriversAndUnder25 = 'Additional drivers and under 25',
}
